import {
  addTrackTag,
  excludedPathsUrl,
  fetchAnalysisStatus,
  fetchLibrary,
  fetchTags,
  fetchTracks,
  removeTrackTag,
  setBpmOverride,
  setExcluded,
  startScan,
  type SortKey,
  type Tag,
  type Track,
} from "../api.js";
import { duration, escapeHtml, tempo } from "./format.js";

const PAGE_SIZE = 200;
const COLUMNS: Array<{ key: SortKey; label: string; className?: string }> = [
  { key: "title", label: "Title" },
  { key: "artist", label: "Artist" },
  { key: "album", label: "Album" },
  { key: "duration", label: "Time", className: "num" },
  { key: "bpm", label: "BPM", className: "num" },
];
/** Tags and the exclude action are not sortable, so they sit outside COLUMNS. */
const EXTRA_COLUMNS = 2;

export class LibraryBrowser {
  private search = "";
  private sort: SortKey = "artist";
  private order: "asc" | "desc" = "asc";
  private offset = 0;
  private total = 0;
  private searchTimer: number | undefined;
  private pollTimer: number | undefined;
  /** null = whole library; a name = only tracks carrying it. */
  private tag: string | null = null;
  /** The junk drawer: shows *only* excluded tracks, so they can be restored. */
  private showExcluded = false;
  private tags: Tag[] = [];

  constructor(
    private readonly root: HTMLElement,
    private readonly onSelect: (track: Track) => void,
    /** Told when the tag filter changes, so auto-mix can follow the same pool. */
    private readonly onFilterChange: (tag: string | null) => void = () => {},
  ) {
    this.root.innerHTML = this.chrome();
    this.bind();
  }

  private chrome(): string {
    const headers = COLUMNS.map(
      (c) =>
        `<th data-sort="${c.key}" class="${c.className ?? ""}"><button type="button">${c.label}<span class="caret"></span></button></th>`,
    ).join("");

    return `
      <div class="library-bar">
        <input id="lib-search" type="search" placeholder="Search library" autocomplete="off" spellcheck="false" />
        <select id="lib-tag" title="Show only tracks carrying a tag"></select>
        <button id="lib-junk" type="button" title="Show the tracks you have marked as junk">Junk drawer</button>
        <span class="spacer"></span>
        <span id="lib-count" class="muted"></span>
        <button id="lib-rescan" type="button">Rescan</button>
      </div>
      <div id="lib-junk-bar" class="scan-status" hidden>
        <span class="warn">Junk drawer.</span>
        <span class="muted">These are hidden from the library and never auto-mixed.
        Their files have not been touched.</span>
        <a id="lib-export" href="${excludedPathsUrl}" download="excluded-tracks.txt">Export delete list</a>
      </div>
      <div id="lib-scan" class="scan-status"></div>
      <div id="lib-analysis" class="scan-status"></div>
      <div class="table-wrap">
        <table class="library">
          <thead><tr>${headers}<th>Tags</th><th></th></tr></thead>
          <tbody id="lib-rows"></tbody>
        </table>
      </div>
      <div class="library-foot">
        <button id="lib-prev" type="button">Previous</button>
        <span id="lib-range" class="muted"></span>
        <button id="lib-next" type="button">Next</button>
      </div>
    `;
  }

  private $(id: string): HTMLElement {
    const node = this.root.querySelector(`#${id}`);
    if (!node) throw new Error(`missing #${id}`);
    return node as HTMLElement;
  }

  private bind(): void {
    const search = this.$("lib-search") as HTMLInputElement;
    search.addEventListener("input", () => {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(() => {
        this.search = search.value;
        this.offset = 0;
        void this.refresh();
      }, 200);
    });

    for (const th of this.root.querySelectorAll<HTMLElement>("th[data-sort]")) {
      th.addEventListener("click", () => {
        const key = th.dataset["sort"] as SortKey;
        if (this.sort === key) {
          this.order = this.order === "asc" ? "desc" : "asc";
        } else {
          this.sort = key;
          this.order = "asc";
        }
        this.offset = 0;
        void this.refresh();
      });
    }

    this.$("lib-prev").addEventListener("click", () => {
      this.offset = Math.max(0, this.offset - PAGE_SIZE);
      void this.refresh();
    });
    this.$("lib-next").addEventListener("click", () => {
      if (this.offset + PAGE_SIZE < this.total) {
        this.offset += PAGE_SIZE;
        void this.refresh();
      }
    });

    this.$("lib-rescan").addEventListener("click", () => {
      void this.rescan();
    });

    (this.$("lib-tag") as HTMLSelectElement).addEventListener("change", (event) => {
      const value = (event.target as HTMLSelectElement).value;
      this.tag = value === "" ? null : value;
      this.offset = 0;
      void this.refresh();
      this.onFilterChange(this.tag);
    });

    this.$("lib-junk").addEventListener("click", () => {
      this.showExcluded = !this.showExcluded;
      this.offset = 0;
      void this.refresh();
    });

    // Exclude / restore. A button rather than a keyboard shortcut: it is not
    // destructive, but it does make a track vanish from the list you are
    // looking at, and that should take a deliberate click.
    this.$("lib-rows").addEventListener("click", (event) => {
      const button = (event.target as HTMLElement).closest<HTMLElement>("button[data-exclude]");
      if (!button) return;
      event.stopPropagation();
      const id = Number(button.dataset["exclude"]);
      void this.toggleExcluded(id);
    });

    // Click a tag chip's × to remove it from the track.
    this.$("lib-rows").addEventListener("click", (event) => {
      const chip = (event.target as HTMLElement).closest<HTMLElement>("button[data-untag]");
      if (!chip) return;
      event.stopPropagation();
      const row = chip.closest<HTMLElement>("tr[data-id]");
      if (!row) return;
      void this.removeTag(Number(row.dataset["id"]), chip.dataset["untag"] as string);
    });

    // Single click, not double: the first thing anyone does with a track list
    // is click a row and expect sound.
    this.$("lib-rows").addEventListener("click", (event) => {
      const row = (event.target as HTMLElement).closest<HTMLElement>("tr[data-id]");
      if (!row) return;
      const track = this.byId.get(Number(row.dataset["id"]));
      if (!track) return;
      this.selectedId = track.id;
      this.markSelection();
      this.onSelect(track);
    });

    this.$("lib-rows").addEventListener("dragstart", (event) => {
      const drag = event as DragEvent;
      const row = (drag.target as HTMLElement).closest<HTMLElement>("tr[data-id]");
      if (!row || !drag.dataTransfer) return;
      const id = row.dataset["id"] as string;
      // A custom type so a deck can tell a track drag from anything else the
      // browser might drop on it.
      drag.dataTransfer.setData("application/x-track-id", id);
      drag.dataTransfer.effectAllowed = "copy";
      this.selectedId = Number(id);
      this.markSelection();
    });

    // Double-click a BPM cell to set a manual tempo (A10). This is the escape
    // hatch for material detection cannot handle, so it lives right next to
    // the number it is overriding.
    this.$("lib-rows").addEventListener("dblclick", (event) => {
      const cell = (event.target as HTMLElement).closest<HTMLElement>("td.bpm-cell");
      const row = cell?.closest<HTMLElement>("tr[data-id]");
      if (!cell || !row) return;
      event.stopPropagation();
      this.editBpm(cell, Number(row.dataset["id"]));
    });

    // Double-click the tags cell to add one, mirroring how BPM is edited.
    this.$("lib-rows").addEventListener("dblclick", (event) => {
      const cell = (event.target as HTMLElement).closest<HTMLElement>("td.tags-cell");
      const row = cell?.closest<HTMLElement>("tr[data-id]");
      if (!cell || !row) return;
      event.stopPropagation();
      this.editTag(cell, Number(row.dataset["id"]));
    });
  }

  private async toggleExcluded(trackId: number): Promise<void> {
    const track = this.byId.get(trackId);
    if (!track) return;
    try {
      await setExcluded(trackId, !track.excluded);
      await this.refresh();
    } catch (err) {
      this.$("lib-analysis").innerHTML = `<span class="bad">${escapeHtml(String(err))}</span>`;
    }
  }

  private async removeTag(trackId: number, name: string): Promise<void> {
    try {
      await removeTrackTag(trackId, name);
      await this.refresh();
    } catch (err) {
      this.$("lib-analysis").innerHTML = `<span class="bad">${escapeHtml(String(err))}</span>`;
    }
  }

  /** Add a tag to a track, with completion over the tags that already exist. */
  private editTag(cell: HTMLElement, trackId: number): void {
    if (cell.querySelector("input")) return;
    const previous = cell.innerHTML;

    const input = document.createElement("input");
    input.type = "text";
    input.className = "tag-edit";
    input.placeholder = "tag";
    input.setAttribute("list", "lib-tag-options");
    input.title = "Type a tag and press Enter";

    let settled = false;
    const finish = async (commit: boolean) => {
      if (settled) return;
      settled = true;
      const name = input.value.trim();
      if (!commit || name === "") {
        cell.innerHTML = previous;
        return;
      }
      try {
        await addTrackTag(trackId, name);
        await this.refresh();
      } catch (err) {
        cell.innerHTML = previous;
        this.$("lib-analysis").innerHTML = `<span class="bad">${escapeHtml(String(err))}</span>`;
      }
    };

    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") void finish(true);
      else if (event.key === "Escape") void finish(false);
      event.stopPropagation();
    });
    input.addEventListener("blur", () => void finish(true));

    cell.textContent = "";
    cell.appendChild(input);
    input.focus();
  }

  private editBpm(cell: HTMLElement, trackId: number): void {
    if (cell.querySelector("input")) return;
    const track = this.byId.get(trackId);
    const previous = cell.innerHTML;

    const input = document.createElement("input");
    input.type = "text";
    input.className = "bpm-edit";
    input.value = track?.tempo != null ? track.tempo.toFixed(1) : "";
    input.placeholder = "BPM";
    input.title = "Enter a tempo, or leave empty to clear the override";

    let settled = false;
    const finish = async (commit: boolean) => {
      if (settled) return;
      settled = true;
      if (!commit) {
        cell.innerHTML = previous;
        return;
      }
      const raw = input.value.trim();
      const bpm = raw === "" ? null : Number(raw);
      if (bpm !== null && !Number.isFinite(bpm)) {
        cell.innerHTML = previous;
        return;
      }
      cell.textContent = "…";
      try {
        await setBpmOverride(trackId, bpm);
        await this.refresh();
      } catch (err) {
        cell.innerHTML = previous;
        this.$("lib-analysis").innerHTML = `<span class="bad">${escapeHtml(String(err))}</span>`;
      }
    };

    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") void finish(true);
      else if (event.key === "Escape") void finish(false);
      event.stopPropagation();
    });
    input.addEventListener("blur", () => void finish(true));

    cell.textContent = "";
    cell.appendChild(input);
    input.focus();
    input.select();
  }

  private byId = new Map<number, Track>();
  private selectedId: number | null = null;

  /** The row the deck Load buttons act on. */
  selected(): Track | null {
    return this.selectedId === null ? null : (this.byId.get(this.selectedId) ?? null);
  }

  byTrackId(trackId: number): Track | null {
    return this.byId.get(trackId) ?? null;
  }

  private markSelection(): void {
    for (const row of this.root.querySelectorAll<HTMLElement>("tr[data-id]")) {
      row.classList.toggle("selected", Number(row.dataset["id"]) === this.selectedId);
    }
  }

  private async rescan(): Promise<void> {
    const button = this.$("lib-rescan") as HTMLButtonElement;
    button.disabled = true;
    try {
      await startScan();
      this.pollScan();
    } catch (err) {
      this.$("lib-scan").innerHTML = `<span class="bad">${escapeHtml(String(err))}</span>`;
      button.disabled = false;
    }
  }

  private pollScan(): void {
    window.clearTimeout(this.pollTimer);
    this.pollTimer = window.setTimeout(() => void this.refresh(), 500);
  }

  async refresh(): Promise<void> {
    const [summary, page, tags] = await Promise.all([
      fetchLibrary(),
      fetchTracks({
        q: this.search,
        sort: this.sort,
        order: this.order,
        limit: PAGE_SIZE,
        offset: this.offset,
        // Only ever one of these: the junk drawer is its own view, and a tag
        // filter inside it would just be confusing.
        ...(this.showExcluded ? { excluded: true } : this.tag ? { tag: this.tag } : {}),
      }),
      fetchTags(),
    ]);

    this.tags = tags;
    this.renderTagFilter();
    // The count lives on the button rather than in the track count, so "how
    // much junk is there" and "how do I see it" are the same control.
    this.$("lib-junk").textContent =
      summary.excluded > 0 ? `Junk drawer (${summary.excluded})` : "Junk drawer";
    this.$("lib-junk").classList.toggle("active", this.showExcluded);
    this.$("lib-junk-bar").hidden = !this.showExcluded;
    (this.$("lib-tag") as HTMLSelectElement).disabled = this.showExcluded;

    this.total = page.total;
    this.byId = new Map(page.items.map((t) => [t.id, t]));
    this.renderRows(page.items);
    this.renderHeaders();
    this.markSelection();

    this.$("lib-count").textContent =
      `${page.total} track${page.total === 1 ? "" : "s"}` +
      (summary.missing ? ` · ${summary.missing} missing` : "");

    const from = page.total === 0 ? 0 : this.offset + 1;
    const to = Math.min(this.offset + page.items.length, page.total);
    this.$("lib-range").textContent = page.total ? `${from}–${to} of ${page.total}` : "";
    (this.$("lib-prev") as HTMLButtonElement).disabled = this.offset === 0;
    (this.$("lib-next") as HTMLButtonElement).disabled = to >= page.total;

    await this.renderAnalysis();

    const scan = summary.scan;
    const rescan = this.$("lib-rescan") as HTMLButtonElement;
    rescan.disabled = scan.running;
    if (scan.running) {
      this.$("lib-scan").innerHTML =
        `<span class="working">Scanning…</span> ${scan.seen} seen · ${scan.added} added`;
      this.pollScan();
    } else if (scan.errors) {
      this.$("lib-scan").innerHTML =
        `<span class="warn">${scan.errors} file${scan.errors === 1 ? "" : "s"} could not be read.</span>` +
        ` <span class="muted">${escapeHtml(scan.failed.slice(0, 3).join(", "))}</span>`;
    } else if (scan.last_error) {
      this.$("lib-scan").innerHTML = `<span class="bad">${escapeHtml(scan.last_error)}</span>`;
    } else {
      this.$("lib-scan").textContent = "";
    }
  }

  private async renderAnalysis(): Promise<void> {
    const node = this.$("lib-analysis");
    try {
      const status = await fetchAnalysisStatus();
      if (status.pending > 0 || status.running) {
        const current = status.current_path ? ` — ${escapeHtml(status.current_path)}` : "";
        node.innerHTML =
          `<span class="working">Analysing</span> ${status.pending} to go${current}`;
        // Shares pollTimer with the scan poll so repeated refreshes cannot
        // stack timers. A running scan reschedules this at its own faster rate.
        window.clearTimeout(this.pollTimer);
        this.pollTimer = window.setTimeout(() => void this.refresh(), 2000);
      } else if (status.failed > 0) {
        node.innerHTML =
          `<span class="warn">${status.failed} track${status.failed === 1 ? "" : "s"} failed analysis.</span>`;
      } else {
        node.textContent = "";
      }
    } catch {
      node.textContent = "";
    }
  }

  private renderHeaders(): void {
    for (const th of this.root.querySelectorAll<HTMLElement>("th[data-sort]")) {
      const active = th.dataset["sort"] === this.sort;
      th.classList.toggle("sorted", active);
      const caret = th.querySelector(".caret");
      if (caret) caret.textContent = active ? (this.order === "asc" ? "▲" : "▼") : "";
    }
  }

  /** The tag dropdown, plus the datalist the per-row tag editor completes from. */
  private renderTagFilter(): void {
    const select = this.$("lib-tag") as HTMLSelectElement;
    const options = [
      `<option value="">All tags</option>`,
      ...this.tags.map(
        (t) =>
          `<option value="${escapeHtml(t.name)}"${t.name === this.tag ? " selected" : ""}>` +
          `${escapeHtml(t.name)} (${t.track_count})</option>`,
      ),
    ];
    // A tag filter that is set but no longer exists would otherwise vanish
    // silently and show the whole library as though nothing were filtered.
    if (this.tag && !this.tags.some((t) => t.name === this.tag)) {
      options.push(`<option value="${escapeHtml(this.tag)}" selected>${escapeHtml(this.tag)} (0)</option>`);
    }
    select.innerHTML = options.join("");

    let datalist = document.getElementById("lib-tag-options") as HTMLDataListElement | null;
    if (!datalist) {
      datalist = document.createElement("datalist");
      datalist.id = "lib-tag-options";
      document.body.appendChild(datalist);
    }
    datalist.innerHTML = this.tags
      .map((t) => `<option value="${escapeHtml(t.name)}"></option>`)
      .join("");
  }

  private renderRows(tracks: Track[]): void {
    const span = COLUMNS.length + EXTRA_COLUMNS;
    if (tracks.length === 0) {
      const reason = this.showExcluded
        ? "Nothing is marked as junk."
        : this.tag
          ? `No tracks tagged “${escapeHtml(this.tag)}”.`
          : this.search
            ? `Nothing matches “${escapeHtml(this.search)}”.`
            : "No tracks. Check the library path, then rescan.";
      this.$("lib-rows").innerHTML =
        `<tr><td colspan="${span}" class="empty muted">${reason}</td></tr>`;
      return;
    }

    this.$("lib-rows").innerHTML = tracks
      .map((t) => {
        const bpm = tempo(t);
        const classes = [t.missing ? "missing" : "", t.excluded ? "excluded" : ""]
          .filter(Boolean)
          .join(" ");
        const chips = t.tags
          .map(
            (name) =>
              `<span class="tag-chip">${escapeHtml(name)}` +
              `<button type="button" data-untag="${escapeHtml(name)}" title="Remove this tag">×</button></span>`,
          )
          .join("");
        // Verb phrases, not nouns. An earlier cut labelled this button "Junk",
        // which in a column of identical buttons reads as a status badge on
        // every row rather than something you can click - the whole library
        // looked like it had been marked junk.
        // The act-* class is what lets a phone swap these verbs for something
        // that fits a 55px column without the markup carrying two labels.
        const action = t.excluded
          ? `<button type="button" class="ghost act-restore" data-exclude="${t.id}" title="Put this back in the library">Restore</button>`
          : `<button type="button" class="ghost act-junk" data-exclude="${t.id}" title="Hide from the library and auto-mix. The file itself is not touched.">Mark junk</button>`;

        return `<tr data-id="${t.id}" draggable="true"${classes ? ` class="${classes}"` : ""}>
          <td class="title">${escapeHtml(t.title ?? "")}</td>
          <td>${escapeHtml(t.artist ?? "")}</td>
          <td>${escapeHtml(t.album ?? "")}</td>
          <td class="num">${duration(t.duration)}</td>
          <td class="num bpm-cell ${bpm.className}" title="${escapeHtml(bpm.title)} — double-click to set manually">${escapeHtml(bpm.text)}</td>
          <td class="tags-cell" title="Double-click to add a tag">${chips}</td>
          <td class="row-actions">${action}</td>
        </tr>`;
      })
      .join("");
  }
}
